import {
  Alert,
  Button,
  Card,
  CircularProgress,
  darken,
  keyframes,
  SwipeableDrawer,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import * as React from 'react';
import { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactPlayer from 'react-player';
import { makeStyles } from 'tss-react/mui';
import { AppConstantsContext } from '../../../../context';
import { TranscriptionStoreContext } from '../../../../context/TranscriptionStoreContext/TranscriptionStoreContext';
import type { ExportType } from '../../../../entities';
import { JobType, TranscriptionState } from '../../../../entities';
import useLocaleNavigate from '../../../../hooks/useLocalNavigation';
import { getErrorMessage, localeUrl } from '../../../../utils';
import SimpleAudioPlayer from './SimpleAudioPlayer';
import SongCardActions from './SongCardActions';

const KeyFrameBlinker = keyframes`
  50% { opacity: 0.3 }
`;

const useStyles = makeStyles()((theme) => ({
  card: {
    borderRadius: 12,
    background: '#393939',
    color: '#fff',
    padding: '2rem',
    maxWidth: '44em',
    boxShadow: theme.shadows[6],
    display: 'flex',
    flexDirection: 'column',
    gap: '1.1rem',
    [theme.breakpoints.down('md')]: {
      boxShadow: 'none',
      background: 'transparent',
    },
  },
  infoAudio: {
    display: 'flex',
    // gridTemplateColumns: '5em auto',
    alignItems: 'center',
    gap: theme.spacing(2),
  },
  infoVideo: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1.1rem',
  },
  video: {
    order: 1,
    alignSelf: 'center',
  },
  title: {
    fontWeight: 500,
    fontSize: '1.25rem',
    lineHeight: 1.2,
    [theme.breakpoints.down('md')]: {
      fontSize: '1rem',
    },
  },
  subtitle: {
    color: '#ccc',
    fontSize: '1rem',
    [theme.breakpoints.down('md')]: {
      fontSize: '.9rem',
    },
  },
  previewBox: {
    border: `3px solid ${theme.palette.primary.main}`,
    borderRadius: 12,
    overflow: 'hidden',
    position: 'relative',
    background: '#fff',
    minHeight: 180,
    maxHeight: 200,
    margin: '1em 0',
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'center',
  },

  openSheetButton: {
    position: 'absolute',
    bottom: '5%',
    borderRadius: 12,
    padding: '0.75rem 2.5rem',
    fontSize: '1.25rem',
    fontWeight: 600,
    color: '#fff',
    boxShadow: 'none',
    background: `linear-gradient(to top, 
      ${darken(theme.palette.primary.main, 0.25)} 0%, 
      ${theme.palette.primary.main} 100%)`,

    '&:hover': {
      background: `linear-gradient(to top, 
        ${darken(theme.palette.primary.main, 0.7)} 0%, 
        ${theme.palette.primary.main} 100%)`,
    },
  },
  openSheetMobile: {
    width: '100%',
    marginTop: '1rem',
    borderRadius: 12,
    padding: '0.75rem 2.5rem',
    fontSize: '1.25rem',
    fontWeight: 600,
    color: '#fff',
    boxShadow: 'none',
    background: `linear-gradient(to top, 
      ${darken(theme.palette.primary.main, 0.25)} 0%, 
      ${theme.palette.primary.main} 100%)`,
    maxWidth: '29em',
  },
  date: {
    color: '#ffffff82',
    [theme.breakpoints.down('md')]: {
      fontSize: '.8rem',
    },
  },
  progressWrapper: {
    margin: 'auto auto',
    position: 'relative',
    width: '100px',
    height: '100px',
  },

  progressText: {
    fontSize: '1.2rem',
    fontWeight: 600,
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    animation: `${KeyFrameBlinker} 1.4s linear infinite`,
  },
  progressTextBlinking: {
    textAlign: 'center',
    animation: `${KeyFrameBlinker} 1.4s linear infinite`,
  },
}));

interface SongCardProps {
  preview?: React.ReactNode;
  handleUnlockTranscription: (option: ExportType) => void;
}

const SongCard: React.FC<SongCardProps> = ({ preview, handleUnlockTranscription }) => {
  const { classes } = useStyles();
  // eslint-disable-next-line max-len
  const { selectedTranscription, showSelectedOnMobile, setShowSelectedOnMobile, transcriptions } = useContext(TranscriptionStoreContext);
  const { t } = useTranslation();

  const { navigateTo } = useLocaleNavigate();
  const [openYouTube, setOpenYouTube] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { jobType } = useContext(AppConstantsContext);
  const showPlayer = (jobType !== JobType.TranscribeScan
      && selectedTranscription?.state !== TranscriptionState.Refunded)
    || selectedTranscription?.videoUrl;

  const onOpenSheet = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();

    const isNewTab = event.metaKey || event.ctrlKey || event.altKey || event.button === 1;

    if (isNewTab) {
      const url = localeUrl(`/file?id=${selectedTranscription?.id}`);
      window.open(url, '_blank');
      return;
    }
    setShowSelectedOnMobile(false);
    navigateTo(`/file?id=${selectedTranscription?.id}`);
  };

  if (!selectedTranscription) {
    return null;
  }

  const cardContent = (
    <Card
      data-testid={`songcard-${selectedTranscription?.id}`}
      className={classes.card}
      style={{ marginTop: transcriptions?.length > 1 ? '6.5rem' : 0 }}
    >
      <div className={openYouTube ? classes.infoVideo : classes.infoAudio}>
        {showPlayer
          && (selectedTranscription?.videoUrl ? (
            <div className={openYouTube ? classes.video : ''}>
              <ReactPlayer
                width="60px"
                light
                src={selectedTranscription?.videoUrl}
                onClickPreview={() => setOpenYouTube(true)}
              />
            </div>
          ) : (
            <SimpleAudioPlayer />
          ))}

        <div>
          <div className={classes.title} data-testid="songcard-title">
            {selectedTranscription?.title}
          </div>
          <div className={classes.subtitle} data-testid="songcard-composer">
            {selectedTranscription?.composer}
          </div>
          <div className={classes.date} data-testid="songcard-creation-date">
            {new Date(selectedTranscription.creationDate).toLocaleString()}
          </div>
        </div>
      </div>

      {selectedTranscription?.state === TranscriptionState.Enqueued
        || (selectedTranscription?.state === TranscriptionState.Processing && (
          <>
            <div className={classes.progressWrapper}>
              <CircularProgress
                style={{ position: 'absolute' }}
                size={100}
                value={100}
                variant="determinate"
                disableShrink
                thickness={8}
                sx={{ color: '#979797' }}
              />
              <CircularProgress
                size={100}
                style={{ position: 'absolute' }}
                variant="determinate"
                value={selectedTranscription.progress}
                disableShrink
                thickness={8}
                color="primary"
              />
              <Typography className={classes.progressText} variant="body2" color="textPrimary">
                {selectedTranscription.progress.toFixed(0)}
                {' '}
                %
              </Typography>
            </div>
            <Typography variant="body2" className={classes.progressTextBlinking}>
              {t('home.songbook.progress')}
            </Typography>
          </>
        ))}
      {selectedTranscription?.state === TranscriptionState.Ok && (
        <div>
          {isMobile ? (
            <Button
              variant="contained"
              id="btn_card_open_sheet"
              className={classes.openSheetMobile}
              onMouseDown={onOpenSheet}
            >
              {t('home.songbook.open')}
            </Button>
          ) : (
            <div>
              <div className={classes.previewBox}>
                {preview}
                <Button
                  variant="contained"
                  className={classes.openSheetButton}
                  onClick={onOpenSheet}
                >
                  {t('home.songbook.open')}
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
      {(selectedTranscription?.state === TranscriptionState.Error
        || selectedTranscription?.error) && (
        <Alert severity="error">
          <Typography variant="body2" color="textPrimary">
            {getErrorMessage(selectedTranscription?.error, selectedTranscription?.source, t)}
          </Typography>
        </Alert>
      )}
      {selectedTranscription?.state === TranscriptionState.Refunded
        && !selectedTranscription?.error && (
          <Alert severity="info">
            <Typography variant="body2" color="textPrimary">
              {t('home.songbook.refunded')}
            </Typography>
          </Alert>
      )}
      <SongCardActions handleUnlockTranscription={handleUnlockTranscription} />
    </Card>
  );

  if (isMobile && showSelectedOnMobile) {
    return (
      <SwipeableDrawer
        onOpen={() => {}}
        data-testid="songcard-drawer"
        anchor="right"
        PaperProps={{
          sx: {
            width: '90%',
            maxWidth: '38em',
          },
        }}
        open
        onClose={() => setShowSelectedOnMobile(false)}
      >
        {cardContent}
      </SwipeableDrawer>
    );
  }
  if (!isMobile) {
    return cardContent;
  }
  return null;
};

export default SongCard;
